import React, { lazy, Suspense } from "react";

import LoadingPage from "Dashboard/Components/LoadingPage";
import ConciergeChat from "Features/ConciergeChat/ConciergeChat";
import { employerDashboardActions } from "Features/ConciergeChat/EmployerDashboard/catalog";
import { connect } from "react-redux";
import { Switch, Route, Redirect, useHistory } from "react-router-dom";
import { FourOhFour } from "ZeroRuby";

import type { State } from "./store";

const AddEmployeeApp = lazy(() => import("./Components/AddEmployee/App"));
const AutopaySetupGuides = lazy(() => import("./Components/AutopaySetupGuides"));
const CancelCompanyPolicyPage = lazy(() => import("./Components/CancelCompanyPolicyPage"));
const CompanyInformationApp = lazy(() => import("./Components/CompanyInformation/App"));
const CostOfNewHireApp = lazy(() => import("./Components/CostOfNewHire/App"));
const EmployeeStatusPage = lazy(() => import("./Components/EmployeeStatusPage/EmployeeStatusPage"));
const HealthPlanDetailsHub = lazy(() => import("./Components/HealthPlanDetails"));
const HomePage = lazy(() => import("./Components/HomePage"));
const ManageAdminsPage = lazy(() => import("./Components/ManageAdminsPage"));
const ManageEmployeesPage = lazy(() => import("./Components/ManageEmployees/ManageEmployeesPage"));
const PartnerAddEmployeeApp = lazy(() => import("./Components/PartnerAddEmployee/App"));
const PolicyManagement = lazy(() => import("./Components/PolicyManagement/PolicyManagementPage"));
const ContributionWizard = lazy(() => import("./Components/ContributionWizard/ContributionWizardPage"));
const ManageDeductionsPage = lazy(() => import("./Components/ManageDeductions/ManageDeductionsPage"));
const Integrations = lazy(() => import("./Components/Integrations/App"));
const PayMyBills = lazy(() => import("./Components/PayMyBills/PayMyBills"));
const RequestInvoice = lazy(() => import("./Components/RequestInvoice"));
const SuccessPage = lazy(() => import("./Components/SuccessPage"));

type EmployerDashboardProps = {
  isLoaded: boolean;
};

const EmployerDashboardDumb = ({ isLoaded }: EmployerDashboardProps) =>
  isLoaded ? (
    <Suspense fallback={<LoadingPage />}>
      <Switch>
        <Route path="/employer-dashboard" exact children={(props) => <HomePage {...props} />} />

        <Route path="/employer-dashboard/add-new-employee" children={(props) => <AddEmployeeApp {...props} />} />

        <Route path="/employer-dashboard/partner-add-employee-confirmation" children={(props) => <PartnerAddEmployeeApp {...props} />} />

        <Route path="/employer-dashboard/company-information/manage-admins" children={(props) => <ManageAdminsPage {...props} />} />

        <Route path="/employer-dashboard/manage-employees" children={(props) => <ManageEmployeesPage {...props} />} />

        <Route path="/employer-dashboard/manage-payroll" children={(props) => <ManageDeductionsPage {...props} />} />

        <Route path="/employer-dashboard/integrations" children={(props) => <Integrations {...props} />} />

        <Route path="/employer-dashboard/company-information" children={(props) => <CompanyInformationApp {...props} />} />

        <Route path="/employer-dashboard/cost-of-new-hire" children={(props) => <CostOfNewHireApp {...props} />} />

        <Route path="/employer-dashboard/success" children={(props) => <SuccessPage {...props} />} />

        <Route path="/employer-dashboard/manage-policy" children={(props) => <PolicyManagement {...props} />} />

        <Route path="/employer-dashboard/contribution-wizard" children={(props) => <ContributionWizard {...props} />} />

        <Route path="/employer-dashboard/health-plan-details" children={(props) => <HealthPlanDetailsHub {...props} />} />

        <Route path="/employer-dashboard/request-invoices" children={(props) => <RequestInvoice {...props} />} />

        <Route path="/employer-dashboard/pay-my-bills" children={(props) => <PayMyBills {...props} />} />

        <Route path="/employer-dashboard/setup-autopay-guides" children={(props) => <AutopaySetupGuides {...props} />} />

        <Route path="/employer-dashboard/cancel-company-policy" children={(props) => <CancelCompanyPolicyPage {...props} />} />

        <Route path="/employer-dashboard/employee-status/:id" children={(props) => <EmployeeStatusPage {...props} />} />

        <Route children={<FourOhFour />} />
      </Switch>
    </Suspense>
  ) : (
    <LoadingPage />
  );

// Renders the dashboard routes plus the Concierge chat widget. Split out so the
// widget can read router history (useHistory) without touching the route tree.
const EmployerDashboardWithConcierge = ({ isLoaded }: EmployerDashboardProps) => {
  const history = useHistory();

  return (
    <>
      <EmployerDashboardDumb isLoaded={isLoaded} />
      {isLoaded && <ConciergeChat actions={employerDashboardActions} flag="concierge_chat_employer_dashboard" history={history} />}
    </>
  );
};

const mapStateToProps = (state: State) => {
  const isLoaded = state.accountId !== "";
  return {
    isLoaded,
  };
};

const withRedux = connect(
  mapStateToProps,
  undefined,
  undefined,
  // This component isn't pure because it relies on the browser URL rather than
  // the Redux store.
  { pure: false },
);

const EmployerDashboard = withRedux(EmployerDashboardWithConcierge);

export default EmployerDashboard;
